import type { Router } from 'vue-router';
import { ElMessage } from 'element-plus';

import { useAuthStore } from '@/stores/auth';
import { getTokenRemainingMs } from '@/utils/security';

/**
 * 路由守卫元信息类型（与各路由 meta 字段对齐）。
 *
 * - requiresAuth：true 表示该路由必须登录才能访问
 * - requiresAdmin：true 表示该路由仅 admin 可访问
 * - title：可选，页面标题（document.title）
 */
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    requiresAdmin?: boolean;
    title?: string;
  }
}

/**
 * 安装全局路由守卫。
 *
 * 四条规则（D7：tender-sage 与 web-feedback 共用同一份 JWT）：
 * 0. OAuth `?token=xxx` 回调拦截：任意路由先处理 token（写入 store + fetchMe）
 *    → 老前端 auth-main.js:40-68 在主页 onMounted 拦截，新 Portal 守卫层统一拦截
 *    → 避免 OAuth 回调指向 /wb/home?token=xxx 时被规则 1 当作未登录重定向
 * 1. 未登录访问 requiresAuth 页 → 重定向登录页（带 redirect 回跳参数）
 *    - requiresAdmin 页 → `/wb/admin/login?redirect=<原 fullPath>`（admin 入口一致）
 *    - 其他 → `/wb/login?redirect=<原 fullPath>`
 * 2. 已登录但非 admin 访问 requiresAdmin → 重定向 `/wb/home` + ElMessage 警告
 * 3. `/wb/login` 已登录访问 → 重定向 `/`（避免重复登录）
 *
 * 与 http.ts 401 拦截器的职责划分：
 * - http.ts 401 拦截器：负责 API 调用过程中发现 token 失效时的兜底跳转
 *   - admin 区域（/wb/admin/*）→ /wb/admin/login
 *   - 其他 → /wb/login
 * - 路由守卫：负责路由切换时的入口校验（避免页面加载时闪烁）
 *
 * @param router 应用路由实例
 */
export function setupRouterGuards(router: Router): void {
  router.beforeEach(async (to, _from) => {
    const auth = useAuthStore();

    // 规则 0：OAuth `?token=xxx` 回调拦截（任意路由都先处理）
    // P1-1 / audit-login D-L7a 修复：
    // 老前端 auth-main.js:40-68 在主页 onMounted 拦截 ?token=，新 Portal 仅 LoginView 拦截
    // → 若 OAuth 回调指向 /wb/home?token=xxx（而非 /wb/login?token=xxx），
    //   guards 规则 1 会因 store 未登录重定向到 /wb/login?redirect=...，
    //   LoginView 的 handleOAuthCallback 只看 route.query.token，redirect 中的 token 不会被识别 → 循环。
    // 修复：在守卫入口统一拦截 ?token=，写入 store + 拉 me，后续逻辑正常进入目标页。
    // 仅当当前路由确实带 token query 时触发（避免对无 token 的常规导航造成副作用）。
    const oauthToken = typeof to.query.token === 'string' ? to.query.token : '';
    if (oauthToken && !auth.isLoggedIn) {
      auth.setToken(oauthToken);
      try {
        await auth.fetchMe();
        if (auth.user) {
          // 清掉 URL 中的 token（避免泄露 + 方便分享），保留其他 query
          const cleanedQuery = { ...to.query };
          delete cleanedQuery.token;
          return { path: to.path, query: cleanedQuery, replace: true };
        }
        // fetchMe 返回但 user 为空 → token 无效，清掉 + 落入未登录分支
        auth.clear();
      } catch {
        // fetchMe 抛错（401 / 网络异常）→ token 无效或后端不可达，清掉 token
        auth.clear();
      }
    }

    // 规则 3a：已登录访问 /wb/login → 跳首页（普通用户邀请码登录页）
    if (to.name === 'login' && auth.isLoggedIn) {
      return { path: '/', replace: true };
    }

    // 规则 4：token 快过期检测（P-007 安全加固）
    // 后端无 refresh endpoint，token 快过期时（剩余 < 1h）直接跳登录页重新认证，
    // 避免用户在受保护页操作到一半遇到 401 中断。
    // 阈值 1h：权衡「用户操作完整性」与「重新认证频率」，留足用户完成在途操作的时间。
    // 边界：
    //   - 剩余为 NaN（token 格式异常或无 exp 字段）→ 按"快过期"处理，跳登录
    //   - 剩余 < 0（已过期）→ 跳登录（与 401 拦截器等价，但更早触发）
    //   - 仅在受保护路由触发（避免登录页 / OAuth 回调被误拦截）
    if (auth.token) {
      const remaining = getTokenRemainingMs(auth.token);
      const ONE_HOUR_MS = 60 * 60 * 1000;
      // 跳过登录页本身（admin login / 普通登录），避免在登录入口形成循环
      const isLoginRoute = to.name === 'login' || to.name === 'admin-login';
      const isAdminArea = to.fullPath.startsWith('/admin');
      const loginPath = isAdminArea ? '/admin/login' : '/login';
      // requiresAuth 路由才会触发快过期跳登录（公开页即使 token 快过期也让用户继续浏览）
      const willRequireAuth = to.matched.some((r) => r.meta.requiresAuth === true);
      if (willRequireAuth && !isLoginRoute && (Number.isNaN(remaining) || remaining < ONE_HOUR_MS)) {
        // 不直接调 auth.logout（避免发 /api/auth/logout 请求），手动 clear + 广播
        auth.clear();
        auth.broadcastAuthEvent('logout');
        ElMessage.warning('登录已接近过期，请重新登录');
        return {
          path: loginPath,
          query: { redirect: to.fullPath },
        };
      }
    }

    // 规则 3b：已登录 admin 访问 /admin/login → 跳 /admin（避免重复登录）
    // 已登录非 admin 访问 → 跳 /（管理员登录页对普通用户无意义）
    if (to.name === 'admin-login' && auth.isLoggedIn) {
      if (auth.isAdmin) {
        return { path: '/admin', replace: true };
      }
      return { path: '/', replace: true };
    }

    // 规则 1：未登录访问需要鉴权的页面 → 跳登录页（带 redirect 回跳参数）
    // 注意：admin 页 401 跳 /admin/login（由 http.ts 401 拦截器处理），此处仅处理首次访问
    // P1-2 关联修复：requiresAdmin 页未登录时也应跳 /admin/login（保持入口一致）
    const requiresAuth = to.matched.some((r) => r.meta.requiresAuth === true);
    if (requiresAuth && !auth.isLoggedIn) {
      const requiresAdmin = to.matched.some((r) => r.meta.requiresAdmin === true);
      const loginPath = requiresAdmin ? '/admin/login' : '/login';
      return {
        path: loginPath,
        query: { redirect: to.fullPath },
      };
    }

    // 规则 2：requiresAdmin 路由的 admin 校验
    const requiresAdmin = to.matched.some((r) => r.meta.requiresAdmin === true);
    if (requiresAdmin && !auth.isAdmin) {
      ElMessage.warning('该页面仅管理员可访问');
      return { path: '/jobfit/analyze', replace: true };
    }

    return true;
  });

  // 标题同步：路由切换后更新 document.title（与守卫解耦，注册在 afterEach）
  router.afterEach((to) => {
    const title = to.meta?.title;
    if (title) {
      document.title = `${title} · lihanlin.cn`;
    } else {
      document.title = 'lihanlin.cn';
    }
  });
}
